import React, { useState } from 'react';
import { ArrowUp, Sparkles, Paperclip, Mic } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import { matchWorkers } from '../api';
import MatchResultsDialog from './MatchResultsDialog';

const SUGGESTIONS = [
  'A sales rep to book meetings from cold outreach',
  'A designer who ships branded assets on Canva',
  'An accountant to reconcile expenses weekly',
  'A recruiter to source candidates on LinkedIn',
];

const HeroSection = () => {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [lastPrompt, setLastPrompt] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const prompt = value.trim();
    if (!prompt) {
      toast({ title: 'Describe your need', description: 'Tell us what your next hire should do.' });
      return;
    }
    setLastPrompt(prompt);
    setLoading(true);
    setResult(null);
    setOpen(true);
    try {
      const data = await matchWorkers(prompt);
      setResult(data);
    } catch (err) {
      toast({
        title: 'Matching failed',
        description: err?.response?.data?.detail || 'Please try again in a moment.',
      });
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-32 left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-gradient-to-b from-neutral-100 to-transparent blur-3xl" />
      </div>

      <div className="mx-auto max-w-4xl px-6 pb-10 pt-20 text-center sm:pt-28">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs text-neutral-600 shadow-sm">
          <Sparkles className="h-3.5 w-3.5 text-neutral-500" />
          <span>AI workers on demand — custom-built for your tasks</span>
        </div>

        <h1 className="font-serif text-5xl leading-[1.05] tracking-tight text-neutral-900 sm:text-6xl md:text-7xl">
          Who is your <span className="italic text-neutral-500">next hire</span>?
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-base text-neutral-600 sm:text-lg">
          Describe what you need below — or scroll for the ready-to-hire roster.
        </p>

        <form onSubmit={handleSubmit} className="group mx-auto mt-10 w-full max-w-2xl">
          <div className="relative rounded-2xl border border-neutral-200 bg-white p-2 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-8px_rgba(0,0,0,0.08)] transition-shadow focus-within:shadow-[0_1px_2px_rgba(0,0,0,0.06),0_16px_40px_-12px_rgba(0,0,0,0.15)]">
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. I need a marketing analyst that produces weekly LinkedIn reports…"
              rows={3}
              disabled={loading}
              className="w-full resize-none rounded-xl bg-transparent px-4 py-3 text-[15px] text-neutral-900 placeholder:text-neutral-400 focus:outline-none disabled:opacity-60"
            />
            <div className="flex items-center justify-between px-2 pb-1 pt-1">
              <div className="flex items-center gap-1">
                <button type="button" className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800" aria-label="Attach">
                  <Paperclip className="h-4 w-4" />
                </button>
                <button type="button" className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800" aria-label="Voice">
                  <Mic className="h-4 w-4" />
                </button>
              </div>
              <Button type="submit" size="icon" disabled={loading} className="h-9 w-9 rounded-full bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60">
                <ArrowUp className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </form>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setValue(s)}
              className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs text-neutral-600 transition-colors hover:border-neutral-300 hover:bg-neutral-50 hover:text-neutral-900"
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-6">
        <div className="flex items-center gap-4">
          <div className="h-px flex-1 bg-neutral-200" />
          <span className="text-xs uppercase tracking-widest text-neutral-400">Or pick a pre-made worker</span>
          <div className="h-px flex-1 bg-neutral-200" />
        </div>
      </div>

      <MatchResultsDialog open={open} onOpenChange={setOpen} loading={loading} result={result} prompt={lastPrompt} />
    </section>
  );
};

export default HeroSection;
